import React, { useEffect } from 'react';
import {
    Button,
    Form,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from 'reactstrap';
import DraggableModal from 'components/common/DraggableModal';
import BlockUi from 'react-block-ui';
import { ScaleLoader as Loader } from 'react-spinners';
import uniqueId from 'lodash/uniqueId';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormInput } from 'common/form';
import { PasswordResetFormData, PasswordResetModal, passwordResetSchema } from 'store/accountMgmt/types';

interface ResetPasswordModalProps {
    header: string;
    open: boolean;
    locked: boolean;
    source: PasswordResetModal['source'];
    actions: {
        close: () => void;
        submit: (formData: PasswordResetFormData) => void;
    };
}

const ChangePasswordModal: React.FC<ResetPasswordModalProps> = ({ header, open, locked, source, actions }) => {
    const { userId, currentUser, password, confirmPassword, currentPassword } = source;
    const { close, submit } = actions;
    const draggableId = `draggable-${uniqueId()}`;
    const requireCurrentPassword = currentUser?.userId === userId;

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<PasswordResetFormData>({
        resolver: yupResolver(passwordResetSchema),
        context: {
            requireCurrentPassword,
        },
        defaultValues: {
            currentPassword: currentPassword || '',
            password: password || '',
            confirmPassword: confirmPassword || '',
        },
    });

    useEffect(() => {
        reset({
            currentPassword: currentPassword || '',
            password: password || '',
            confirmPassword: confirmPassword || '',
        });
    }, [source, reset]);

    const onClose = () => close();
    const onSubmit = (formData: PasswordResetFormData) => submit(formData);

    return (
        <DraggableModal isOpen={open} toggle={onClose} centered handle={`#${draggableId}`} backdrop="static">
            <BlockUi tag="div" blocking={locked} loader={<Loader color={'var(--first)'} loading={locked} />}>
                <ModalHeader id={draggableId} toggle={onClose} className='move-cursor'>{header}</ModalHeader>
                <ModalBody className='no-cursor px-5'>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        {requireCurrentPassword && (
                            <FormInput
                                name="currentPassword"
                                control={control}
                                id="reset-current-password"
                                label="Current password"
                                placeholder="Current password"
                                type="password"
                                error={errors.currentPassword}
                            />
                        )}
                        <FormInput
                            name="password"
                            control={control}
                            id="reset-password"
                            label="Password"
                            placeholder="Password"
                            type="password"
                            error={errors.password}
                        />
                        <FormInput
                            name="confirmPassword"
                            control={control}
                            id="reset-confirm-password"
                            label="Confirm password"
                            placeholder="Confirm password"
                            type="password"
                            error={errors.confirmPassword}
                        />
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="link" onClick={onClose}>Close</Button>
                    <Button color="primary" className="btn-link-dark" onClick={handleSubmit(onSubmit)}>Reset</Button>
                </ModalFooter>
            </BlockUi>
        </DraggableModal>
    );
};

export default ChangePasswordModal;
